<template>
  <div class="step-layout">
    <ProgressBar :current="currentStep" :total="totalSteps - 1" />

    <!-- Header -->
    <header class="step-header">
      <img :src="logo" alt="TreasureHome" class="step-header__logo" />
      <div v-if="currentStep > 0 && currentStep < totalSteps" class="step-indicator">
        <span class="step-indicator__current">{{ currentStep }}</span>
        <span class="step-indicator__sep">/</span>
        <span class="step-indicator__total">{{ totalSteps - 1 }}</span>
      </div>
      <LangSwitcher />
    </header>

    <!-- Step Container -->
    <div class="step-container" ref="container">
      <div ref="stepEl" class="step-inner" :key="currentStep">
        <WelcomeScreen v-if="currentStep === 0" @start="$emit('next')" />
        <PersonalInfo v-else-if="currentStep === 1" :form-data="formData" @update="relay" />
        <HotelCount v-else-if="currentStep === 2" :form-data="formData" @update="relay" />
        <HotelAddresses v-else-if="currentStep === 3" :form-data="formData" @update="relay" />
        <ChannelManager v-else-if="currentStep === 4" :form-data="formData" @update="relay" />
        <BooleanStep v-else-if="currentStep === 5" step-key="step5" field="hasReception" :form-data="formData" @update="relay" />
        <BooleanStep v-else-if="currentStep === 6" step-key="step6" field="hasStripe" :form-data="formData" @update="relay" />
        <BooleanStep v-else-if="currentStep === 7" step-key="step7" field="hasPhoneSystem" :form-data="formData" @update="relay" />
        <BooleanStep v-else-if="currentStep === 8" step-key="step8" field="wantsKiosk" :form-data="formData" @update="relay" />
        <ThankYou v-else-if="currentStep === 9" />
      </div>
    </div>

    <div v-if="validationMessage" class="step-validation-banner" role="alert">
      {{ validationMessage }}
    </div>

    <!-- Footer Nav -->
    <footer v-if="currentStep > 0 && currentStep < totalSteps" class="step-footer">
      <button class="step-footer__back" @click="$emit('prev')" :disabled="currentStep <= 1">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        {{ $t('nav.back') }}
      </button>
      <span class="step-footer__hint">{{ $t('nav.pressEnter') }}</span>
      <button v-if="currentStep < totalSteps - 1" class="step-footer__next" @click="handleNext">
        {{ $t('nav.next') }}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
      </button>
      <button v-else class="step-footer__next step-footer__next--submit" @click="$emit('submit')">
        {{ $t('nav.submit') }}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
      </button>
    </footer>
  </div>
</template>

<script>
import gsap from 'gsap'
import logo from '../assets/logo.svg'
import ProgressBar from './ProgressBar.vue'
import LangSwitcher from './LangSwitcher.vue'
import WelcomeScreen from './steps/WelcomeScreen.vue'
import PersonalInfo from './steps/PersonalInfo.vue'
import HotelCount from './steps/HotelCount.vue'
import HotelAddresses from './steps/HotelAddresses.vue'
import ChannelManager from './steps/ChannelManager.vue'
import BooleanStep from './steps/BooleanStep.vue'
import ThankYou from './steps/ThankYou.vue'

export default {
  name: 'StepLayout',
  components: { ProgressBar, LangSwitcher, WelcomeScreen, PersonalInfo, HotelCount, HotelAddresses, ChannelManager, BooleanStep, ThankYou },
  props: {
    currentStep: { type: Number, default: 0 },
    totalSteps: { type: Number, default: 9 },
    formData: { type: Object, required: true },
    validationMessage: { type: String, default: '' },
  },
  data() {
    return { logo, direction: 1 }
  },
  watch: {
    currentStep(newVal, oldVal) {
      this.direction = newVal > oldVal ? 1 : -1
      this.$nextTick(() => this.animateStep())
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKey)
    this.animateStep()
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKey)
  },
  methods: {
    relay(payload) {
      this.$emit('update-form', payload)
    },
    handleNext() {
      this.$emit('next')
    },
    animateStep() {
      const el = this.$refs.stepEl
      if (!el) return
      gsap.fromTo(el,
        { opacity: 0, y: this.direction * 60 },
        { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' },
      )
    },
    onKey(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        if (this.currentStep === 0) {
          this.$emit('next')
        } else if (this.currentStep > 0 && this.currentStep < this.totalSteps - 1) {
          this.$emit('next')
        } else if (this.currentStep === this.totalSteps - 1) {
          this.$emit('submit')
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.step-layout {
  position: relative;
  z-index: 10;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
}

.step-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 32px;
  flex-shrink: 0;
  z-index: 20;

  &__logo {
    height: 36px;
    width: auto;
    filter: brightness(1.2) saturate(0.9);
  }
}

.step-indicator {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 5px 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.03em;

  &__current {
    color: var(--gold-light);
    font-variant-numeric: tabular-nums;
  }

  &__sep {
    color: rgba(255, 255, 255, 0.15);
    margin: 0 1px;
  }

  &__total {
    color: rgba(255, 255, 255, 0.3);
    font-variant-numeric: tabular-nums;
  }
}

.step-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 32px;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.step-inner {
  width: 100%;
  max-width: 640px;
  box-sizing: border-box;
}

.step-validation-banner {
  flex-shrink: 0;
  margin: 0 32px 8px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 500;
  text-align: center;
  color: #ffb4b4;
  background: rgba(234, 84, 85, 0.12);
  border: 1px solid rgba(234, 84, 85, 0.25);

  @media (max-width: 600px) {
    margin: 0 16px 8px;
    font-size: 0.78rem;
  }
}

.step-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 32px 24px;
  flex-shrink: 0;

  &__back {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 18px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    background: transparent;
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.82rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
      border-color: rgba(255, 255, 255, 0.25);
      color: rgba(255, 255, 255, 0.8);
    }

    &:disabled {
      opacity: 0.2;
      cursor: not-allowed;
    }
  }

  &__hint {
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.2);
    @media (max-width: 600px) { display: none; }
  }

  &__next {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 28px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--gold), var(--gold-dark));
    color: var(--navy);
    font-size: 0.88rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.25s;
    box-shadow: 0 4px 20px rgba(208, 176, 146, 0.25);

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 28px rgba(208, 176, 146, 0.4);
    }

    &--submit {
      background: linear-gradient(135deg, #28c76f, #48da89);
      box-shadow: 0 4px 20px rgba(40, 199, 111, 0.25);

      &:hover {
        box-shadow: 0 6px 28px rgba(40, 199, 111, 0.4);
      }
    }
  }
}

@media (max-width: 600px) {
  .step-header { padding: 14px 16px; }
  .step-container { padding: 0 16px; }
  .step-footer { padding: 12px 16px 20px; }
  .step-header__logo { height: 28px; }
  .step-indicator { padding: 4px 12px; font-size: 0.72rem; }
}
</style>
